import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AbstractMerchantSupplierComponent } from 'app/core/common/abstract.merchant-supplier.component';
import { jsonValidator } from 'app/core/common/jsonValidator';
import { Country } from 'app/core/models/country';
import { FuellingProvider } from 'app/core/models/fuellingProvider';
import { Items } from 'app/core/models/items';
import { Supplier } from 'app/core/models/supplier';
import { FuellingproviderService } from 'app/core/services/fuellingprovider.service';
import { MerchantService } from 'app/core/services/merchant.service';
import { NotifMessageService } from 'app/core/services/notifmessage.service';
import { PosService } from 'app/core/services/pos.service';
import { SupplierService } from 'app/core/services/supplier.service';
import { NGXLogger } from 'ngx-logger';
import { Observable, Subscription } from 'rxjs';
import { AuthenticatedRouteGuard } from 'security/authenticatedRouteGuard.service';

@Component({
  selector: 'app-fuellingprovider-crud',
  templateUrl: './fuellingprovider-crud.component.html'
})
export class FuellingProviderCrudComponent extends AbstractMerchantSupplierComponent implements OnInit {

  isCreateMode : boolean;
  isEditMode : boolean;
  isDetailsMode : boolean;

  fproviderForm: FormGroup;

  subSave: Subscription;

  isLoading: boolean = true;
  isSaveNeeded: boolean;

  fprovider: FuellingProvider;
  fproviderId : number;
  suppliers: Array<Supplier>;

  countryItems: Array<Country> = Array<Country>();
  fuellingProviderTypeItems: Array<String> = new Array<String>();
  posDataProviderItems: Array<String> = new Array<String>();

  fproviderData : Items<FuellingProvider>;

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    protected posService: PosService,
    private roleGuard: AuthenticatedRouteGuard,
    protected supplierService: SupplierService,
    private fuellingProviderService: FuellingproviderService,
    public router: Router,
    private notifService: NotifMessageService,
    private translateService: TranslateService,
    protected merchantService: MerchantService,
    protected log: NGXLogger,
  ) { super(supplierService, merchantService, posService, log, router); }


  ngOnInit() : void {
    this.fuellingProviderTypeItems = this.getFuellingProviderType();
    this.fproviderId = this.route.snapshot.params['id'];
    this.isCreateMode = !this.fproviderId;

    if(this.isCreateMode) {
      this.isLoading = false;
      this.getSuppliers();
      this.initForm(this.isCreateMode);
    } else {
      if (this.router.url.startsWith("/service/fuellingprovider/edit")) {
        this.isEditMode = true
        this.isSaveNeeded = this.fuellingProviderService.isSaveNeeded = false;
        this.fuellingProviderService.getFuellingProviderById(this.fproviderId).subscribe(fprovider => {
          this.fprovider = fprovider;
          this.getSuppliers();
          this.initForm(this.isCreateMode);
        });
        this.fuellingProviderService.saveSubject.subscribe(isSaveNeededTab => {
          this.isSaveNeeded = isSaveNeededTab[0];
        });
      } else {
        this.isDetailsMode = true
        this.fuellingProviderService.getFuellingProviderById(this.fproviderId).subscribe(fprovider => {
          this.fprovider = fprovider;
          this.isLoading = false;
        });
      }
    }
  }

  initForm(isCreateMode : boolean) : void {
    this.fproviderForm = this.formBuilder.group({
      supplierId: new FormControl(isCreateMode ? '' : this.fprovider.supplierId, Validators.required),
      type: new FormControl(isCreateMode ? '' : this.fprovider.fuellingProviderType, Validators.required),
      providerParameters: new FormControl(isCreateMode ? '' : JSON.stringify(this.fprovider.providerParameters),  Validators.compose([
        Validators.required,
        jsonValidator
      ]))
    });
    if (!isCreateMode) {
      this.subSave = this.fproviderForm.valueChanges.subscribe(() => {
        this.fuellingProviderService.isSaveNeeded = true;
        this.fuellingProviderService.saveSubject.next([this.fuellingProviderService.isSaveNeeded]);
      });
    }

    this.fproviderForm.get("supplierId").valueChanges.subscribe(() => {
      let criteria = {
        supplierId: this.fproviderForm.get("supplierId").value
      }
      if (criteria.supplierId) {
        this.fuellingProviderService.findByCriteria(criteria, {}, true).subscribe(fprovider => {
          this.fproviderData = fprovider;
        });
      }
    });
  }

  ngOnDestroy() : void {
    if (this.subSave) {
      this.subSave.unsubscribe();
    }
  }

  canDeactivate(): Observable<boolean> | boolean {
    if (this.isSaveNeeded) {
      this.notifService.emitModal("SAVE_NEEDED");
      return this.notifService.modalResponseSubject$;
    } else {
      return true;
    }
  }

  get fproviderControl() {
    return this.fproviderForm.controls;
  }

  onSubmitForm() : void {
    let fuellingprovider = new FuellingProvider();

    fuellingprovider.fuellingProviderType = this.fproviderForm.get('type').value;
    fuellingprovider.supplierId = this.fproviderForm.get('supplierId').value;
    fuellingprovider.providerParameters = JSON.parse(this.fproviderForm.get('providerParameters').value);
    this.fuellingProviderService.createFuellingProviderAccount(fuellingprovider).subscribe((fuellingprovider) => {
    this.router.navigate(['/service/fuellingprovider']);
    this.notifService.notifySuccess('FUELLINGPROVIDER_CREATED');
    });
  }

  save() : void {
    let fuellingprovider = Object.assign({}, this.fprovider);

    fuellingprovider.id = this.fproviderId;
    fuellingprovider.fuellingProviderType = this.fproviderForm.get('type').value;
    fuellingprovider.supplierId = this.fproviderForm.get('supplierId').value;
    fuellingprovider.providerParameters = JSON.parse(this.fproviderForm.get('providerParameters').value);

    this.fuellingProviderService.updateFuellingProviderAccount(fuellingprovider).subscribe(() => {
      this.fuellingProviderService.isSaveNeeded = false;
      this.fuellingProviderService.saveSubject.next([this.posService.isSaveNeeded]);
      this.notifService.notifySuccess('SAVED');
    });
  }

  getSuppliers() : void {
    if (this.roleGuard.isEnabled("SUPPLIER_READ")) {
      this.supplierService.getAllSuppliers().subscribe(suppliers => {
        this.suppliers = suppliers.content;
        this.isLoading = false;
      });
    }
  }

  checkSupplierIdDuplication() : boolean  {
    if (this.fproviderData != null) {
      if (this.isCreateMode) {
        return this.fproviderData.content.length > 0 && this.fproviderData.content.filter(fprovider => fprovider.supplierId === this.fproviderForm.get("supplierId").value).length > 0
      }
      return this.fproviderData.content.length > 0 && this.fproviderData.content.filter(fprovider => (fprovider.supplierId === this.fproviderForm.get("supplierId").value) && (this.fprovider.supplierId != this.fproviderForm.get("supplierId").value)).length > 0
    }
  }

  back() : void {
    this.router.navigate(['/service/fuellingprovider']);
  }

  edit(): void {
    this.router.navigate(['/service/fuellingprovider/edit', this.fproviderId]);
  }

}
